import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import Layout from './components/Layout.tsx';
import RestrictedLayout from './components/RestrictedLayout.tsx';
import Landing from './pages/Landing.tsx';
import Dashboard from './pages/Dashboard.tsx';
import Login from './pages/Login.tsx';
import UserApproval from './pages/UserApproval.tsx';
import Collaborations from './pages/Collaborations.tsx';
import CollaborationDetails from './pages/CollaborationDetails.tsx';
import CollaborationForm from './pages/CollaborationForm.tsx';
import Clients from './pages/Clients.tsx';
import ClientDetails from './pages/ClientDetails.tsx';
import Influencers from './pages/Influencers.tsx';
import Resources from './pages/Resources.tsx';
import Account from './pages/Account.tsx';
import SuperAdmin from './pages/SuperAdmin.tsx';
import AgencyDetails from './pages/AgencyDetails.tsx';
import Accounting from './pages/Accounting.tsx';
import Casting from './pages/Casting.tsx';
import Assistant from './pages/Assistant.tsx';
import Todo from './pages/Todo.tsx';
import Recruitment from './pages/Recruitment.tsx';
import DailyBrief from './pages/DailyBrief.tsx';
import InfluencerBrief from './pages/InfluencerBrief.tsx';
import PublicCampaignView from './pages/PublicCampaignView.tsx';
import PublicInfluencerView from './pages/PublicInfluencerView.tsx';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { currentUser, userProfile, loading } = useAuth();
  
  if (loading && !currentUser) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-blue"></div>
      </div>
    );
  }
  
  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  return <>{children}</>;
}

function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  const { currentUser, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-blue"></div>
      </div>
    );
  }
  
  if (!currentUser || currentUser.email !== 'hosni@swipe-upp.com') {
    return <Navigate to="/app" />;
  }

  return <>{children}</>;
}

function SuperAdminOnlyRoute({ children }: { children: React.ReactNode }) {
  const { currentUser, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-blue"></div>
      </div>
    );
  }
  
  if (!currentUser || currentUser.email !== 'hosni@swipe-upp.com') {
    return <Navigate to="/app" />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        {/* Public influencer brief routes */}
        <Route path="/brief/:influencerSlug" element={<InfluencerBrief />} />
        {/* Public influencer view route */}
        <Route path="/influencer/:token" element={<PublicInfluencerView />} />
        {/* Public campaign tracking route */}
        <Route path="/suivi/:token" element={<PublicCampaignView />} />
        <Route
          path="/superadmin"
          element={
            <SuperAdminRoute>
              <SuperAdmin />
            </SuperAdminRoute>
          }
        />
        <Route
          path="/superadmin/user-approval"
          element={
            <SuperAdminRoute>
              <UserApproval />
            </SuperAdminRoute>
          }
        />
        <Route
          path="/superadmin/agency/:agencyId"
          element={
            <SuperAdminRoute>
              <AgencyDetails />
            </SuperAdminRoute>
          }
        />
        <Route
          path="/app"
          element={
            <PrivateRoute>
              <Layout />
            </PrivateRoute>
          }
        >
          <Route index element={<Navigate to="/app/collaborations" replace />} />
          <Route path="dashboard" element={
            <RestrictedLayout permission="dashboard">
              <Dashboard />
            </RestrictedLayout>
          } />
          <Route path="collaborations" element={
            <RestrictedLayout permission="collaborations">
              <Collaborations />
            </RestrictedLayout>
          } />
          <Route path="collaborations/new" element={<CollaborationForm />} />
          <Route path="collaborations/:id" element={<CollaborationDetails />} />
          <Route path="casting" element={
            <RestrictedLayout permission="casting">
              <Casting />
            </RestrictedLayout>
          } />
          <Route path="recruitment" element={
            <RestrictedLayout permission="recruitment">
              <Recruitment />
            </RestrictedLayout>
          } />
          <Route path="daily-brief" element={
            <RestrictedLayout permission="todo">
              <DailyBrief />
            </RestrictedLayout>
          } />
          <Route path="clients" element={
            <RestrictedLayout permission="clients">
              <Clients />
            </RestrictedLayout>
          } />
          <Route path="clients/:id" element={
            <RestrictedLayout permission="clients">
              <ClientDetails />
            </RestrictedLayout>
          } />
          <Route path="influencers" element={
            <RestrictedLayout permission="influencers">
              <Influencers />
            </RestrictedLayout>
          } />
          <Route path="resources" element={
            <RestrictedLayout permission="resources">
              <Resources />
            </RestrictedLayout>
          } />
          <Route path="user-approval" element={<UserApproval />} />
          <Route path="account" element={<Account />} />
          <Route path="accounting" element={
            <RestrictedLayout permission="accounting">
              <Accounting />
            </RestrictedLayout>
          } />
          <Route path="todo" element={
            <RestrictedLayout permission="todo">
              <Todo />
            </RestrictedLayout>
          } />
          <Route 
            path="assistant" 
            element={
              <SuperAdminOnlyRoute>
                <Assistant />
              </SuperAdminOnlyRoute>
            } 
          />
        </Route>
      </Routes>
    </Router>
  );
}

export default App;